import React, { useState } from "react";
import Dialog from "./Dialog";
import { PrimaryButtons } from "./buttons";
import { DeleteIcon } from "../icons";
import { Note } from "src/types/listing";
import ScrollContainer from "../core/ScrollContainer";
import ToolbarButton from "../SearchListings/ToolbarButton";

export interface ManageNotesProps {
  onClose: () => void;
  open: boolean;
  notes: Note[];
  onDeleteClick: (notedId: string) => void;
  onAddClick: (newNoteText: string) => void;
}

const ManageNotes = ({
  onClose,
  open,
  notes = [],
  onDeleteClick,
  onAddClick,
}: ManageNotesProps) => {
  const [newNoteText, setNewNoteText] = useState("");
  const [confirmingDeleteId, setConfirmingDeleteId] = useState<string | null>(null);

  const addNote = async () => {
    if (!newNoteText.trim()) return;
    await onAddClick(newNoteText);
    setNewNoteText("");
  };

  const handleDeleteClick = (noteId: string) => {
    if (confirmingDeleteId === noteId) {
      // Second click - actually delete
      onDeleteClick(noteId);
      setConfirmingDeleteId(null);
    } else {
      // First click - show confirmation
      setConfirmingDeleteId(noteId);
    }
  };

  return (
    <Dialog onOpenChange={onClose} isOpen={open} title="Notes">
      <div className="max-w-[552px] min-h-[176px] py-4 bg-[var(--my-color)]">
        <p className="text-gray-secondary font-semibold leading-5">Add Note</p>
        <div className="flex gap-2 h-[200px] mt-2">
          <textarea
            autoFocus
            className=" relative  w-full bg-surface py-2 px-3 border border-border rounded placeholder:text-gray-secondary text-[14px] leading-6 text-gray-primary outline-primary-hover resize-none"
            placeholder="Enter your note here"
            value={newNoteText}
            onChange={(e) => {
              setNewNoteText(e.target.value);
            }}
          />
        </div>
        <div className="flex justify-end mt-2">
          <PrimaryButtons label="Add Note" onClick={addNote} isDisable={!newNoteText.trim()} />
        </div>
        <div className="mt-4">
          <ScrollContainer>
            <div className="max-h-[200px] gap-1 ">
              {(notes || []).map((note, index) => {
                return (
                  <div
                    key={`note ${index}`}
                    className="flex  justify-between items-baseline p-1 pr-[20px] border-gray-200 gap-2 border-b"
                  >
                    <pre className="text-[13px] text-gray-primary leading-4 max-w-[420px] whitespace-pre-wrap font-sans">
                      {note?.note}
                    </pre>
                    <div className="flex gap-5 items-center min-h-[36px]">
                      <p className="text-[13px] text-gray-secondary leading-4">
                        {note.formattedCreatedAt}
                      </p>
                      {confirmingDeleteId === note.id ? (
                        <div className="flex gap-2 items-center">
                          <button
                            className="px-2 py-1 text-[12px] text-gray-secondary border border-gray-300 rounded hover:bg-gray-100"
                            onClick={() => setConfirmingDeleteId(null)}
                          >
                            Cancel
                          </button>
                          <button
                            className="px-2 py-1 text-[12px] text-white bg-red-600 rounded hover:bg-red-700"
                            onClick={() => handleDeleteClick(note.id)}
                          >
                            Delete
                          </button>
                        </div>
                      ) : (
                        <ToolbarButton
                          icon={<DeleteIcon />}
                          onClick={() => handleDeleteClick(note.id)}
                        />
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </ScrollContainer>
        </div>
      </div>
      <div className="flex justify-center mt-8">
        <PrimaryButtons label="Done" onClick={onClose} />
      </div>
    </Dialog>
  );
};

export default ManageNotes;
